import React, { useEffect, useRef, useState } from "react";
import { AuthProvider, useAuthInfo, useLogoutFunction } from "@propelauth/react";
import { identify, resetIdentity } from "../lib/posthog.js";
import { AuthScreen } from "./AuthScreen.js";
import { propelAuthUrl } from "./config.js";
import { setAccessToken, setUnauthenticatedHandler } from "./token.js";
import { useRecorderHandover } from "../lib/extension.js";

/**
 * Signs the app in, or stands aside entirely.
 *
 * Two arrangements in one component on purpose. With a tenant configured every
 * screen is behind a session; without one the app renders exactly as it did
 * before accounts existed. The second is not a fallback for a broken
 * configuration — it is the mode `docker compose up` runs in, and the API makes
 * the same decision from its own environment, so the two cannot disagree about
 * whether a request should have carried a token.
 */
export const Gate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const url = propelAuthUrl();
  if (!url) return <>{children}</>;

  return (
    <AuthProvider authUrl={url}>
      <RequireSession>{children}</RequireSession>
    </AuthProvider>
  );
};

/**
 * Holds the app back until somebody is signed in, and keeps the token current.
 *
 * Nobody is sent anywhere. Every signed-out state — never signed in, signed out,
 * session expired — renders the same screen, which is the one in this
 * application's own type rather than a generic page on somebody else's domain.
 */
const RequireSession: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuthInfo();
  const [stalled, setStalled] = useState(false);
  const identifiedUserId = useRef<string | null>(null);

  // Long enough that a slow network is not accused of being a misconfiguration,
  // short enough that nobody sits in front of a spinner wondering.
  useEffect(() => {
    if (!auth.loading) {
      setStalled(false);
      return;
    }
    const timer = window.setTimeout(() => setStalled(true), 8000);
    return () => window.clearTimeout(timer);
  }, [auth.loading]);

  /**
   * The token, pushed to the plain module `api.ts` reads.
   *
   * During render, not in an effect, and that is the whole point. React runs a
   * child's effects before its parent's, so a child that fetches on mount goes
   * out *before* a parent effect could have stored the token — and on the very
   * first commit after signing in there is no earlier commit to have stored it.
   * The request leaves with no Authorization header and comes back 401.
   *
   * On an ordinary navigation this is invisible, because some earlier commit
   * stored the token. It bites on a fresh page load, intermittently, depending
   * on whether the SDK resolves the token in the same render that first shows
   * the children. Found on the return from Stripe Checkout, which is always a
   * fresh load: the billing panel fetched once, got a 401, and stayed blank.
   *
   * Writing during render is a side effect, which React would rather nobody
   * did. It is safe in the ways that matter here: it is idempotent, the value
   * comes only from what was rendered with, and nothing reads it during render
   * — so a double invocation under StrictMode, or a render React discards,
   * leaves exactly the same thing behind.
   */
  setAccessToken(auth.loading ? null : (auth.accessToken ?? null));

  /**
   * Who is signed in, told to analytics.
   *
   * The earliest point the browser has the authenticated user. `userId` is
   * PropelAuth's stable primary identifier, and it is the only thing that goes:
   * `@posthog/wizard` also sent the email address as a person property, and the
   * Privacy Policy says analytics is set up "to record product usage, not to
   * identify individuals to third parties". An email address in PostHog makes
   * that sentence false. The id joins one person's sessions together, which is
   * the whole of what analytics needs.
   *
   * Nothing is sent at all unless the visitor accepted — `identify` is a no-op
   * until then, and the answer does not carry over from the marketing site,
   * which is a different origin.
   */
  useEffect(() => {
    if (auth.loading) return;

    if (!auth.isLoggedIn) {
      if (identifiedUserId.current) {
        resetIdentity();
        identifiedUserId.current = null;
      }
      return;
    }

    const { user } = auth;
    if (identifiedUserId.current === user.userId) return;

    // A provider can replace a session without an intervening signed-out render.
    // Start that account from a fresh anonymous identity rather than linking users.
    if (identifiedUserId.current) resetIdentity();
    identify(user.userId);
    identifiedUserId.current = user.userId;
  }, [auth]);

  // No redirect to the hosted page any more: signing in happens here, in the
  // application's own type and colours. PropelAuth still does the
  // authenticating — AuthScreen posts to the same endpoints those pages do.

  // A session that expires while the page is open comes back to *this* screen.
  //
  // It used to leave for PropelAuth's hosted page, which was a reflex from
  // before there was anywhere else to send anyone — and it undid the whole
  // point of building this one. A token going stale is not a different kind of
  // signed-out; it is the state the Gate already renders a login screen for.
  //
  // So: ask the SDK to re-check. If the session really is gone `isLoggedIn`
  // turns false, this component renders <AuthScreen /> below, and somebody
  // signs in again where they already were. If the token merely needed
  // refreshing, nothing visible happens at all, which is the better outcome and
  // was not reachable before.
  useEffect(() => {
    // Returns the promise rather than discarding it: `request()` waits for the
    // refresh and retries once, so a token that had merely gone stale produces
    // a slow call rather than a failed one.
    setUnauthenticatedHandler(() => auth.refreshAuthInfo());
    return () => setUnauthenticatedHandler(null);
  }, [auth]);

  // Not for ever. The SDK retries `refresh_token` on a loop, so an auth URL the
  // browser cannot reach — the wrong tenant, an origin missing from PropelAuth's
  // Frontend Integration, a network with opinions — leaves this stuck on
  // "Checking your session…" with the reason only visible in the console. Found
  // exactly that way: a dev origin that was not on the allow-list produced a
  // page that never resolved and never explained itself.
  if (auth.loading) return stalled ? <ProviderUnreachable /> : <Waiting>Checking your session…</Waiting>;
  if (!auth.isLoggedIn) return <AuthScreen />;

  // Not before the token has been handed over. Rendering a screen first means
  // its first fetch goes out unauthenticated and comes back 401, which the app
  // would show as an error rather than as a moment of waiting.
  if (!auth.accessToken) return <Waiting>Signing you in…</Waiting>;

  return (
    <>
      <RecorderHandover />
      {children}
    </>
  );
};

/**
 * Gives the recorder extension a credential, on every signed-in screen.
 *
 * Here rather than on the dashboard, which was where it started. A new account
 * goes through onboarding and out to the new-project screen, so it can be
 * several minutes and a first recording before it ever sees a dashboard — and
 * the extension would have been signed out for all of it.
 *
 * Costs nothing on the screens that do not need it: the extension is asked
 * whether it wants one, and a browser that already has a credential says no.
 * Which is the point — minting replaces the last token, so handing one over
 * unasked would unpair whichever other browser was holding it.
 *
 * A component rather than a call in `RequireSession`, because that function
 * returns early four times before this point and a hook cannot be conditional.
 */
const RecorderHandover: React.FC = () => {
  useRecorderHandover(true);
  return null;
};

const Waiting: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex min-h-screen items-center justify-center">
    <p className="text-[13px] text-ink-300">{children}</p>
  </div>
);

/**
 * Signing out, for the nav bar.
 *
 * A hook rather than a button, because where it belongs is a question for the
 * shell and not for this file. Null when there is no provider, which is how the
 * shell knows not to offer it.
 */
export function useSignOut(): (() => void) | null {
  // Before the SDK hook, not after. See `useAccount`.
  if (propelAuthUrl() === null) return null;
  const logout = useLogoutFunction();
  return () => void logout(true);
}

/**
 * Who is signed in, for the shell to show.
 *
 * Null when there is no provider at all, which is the open mode `docker compose
 * up` runs in — there is no account to name, and a menu offering to sign out of
 * nothing would be worse than no menu.
 *
 * `email` is the only field promised. PropelAuth supplies first and last names
 * only if the tenant collects them and the person filled them in, so a shell
 * that relied on a name would show a blank chip for most accounts.
 */
export interface Account {
  email: string;
  name: string | null;
}

export function useAccount(): Account | null {
  /**
   * The check comes first, and the order is the whole point.
   *
   * `useAuthInfo` throws when there is no `AuthProvider` above it, and in open
   * mode `Gate` deliberately renders none. So asking the SDK first and then
   * checking whether a provider exists never reached the check — it crashed
   * the whole app with "useAuthInfo must be used within an AuthProvider",
   * which is a confusing way to be told that accounts are switched off.
   *
   * Calling a hook conditionally is normally forbidden because the condition
   * can change between renders and shift the order of every hook after it.
   * This one cannot: it reads `window.VEYSO_CONFIG`, which nginx writes into
   * the page in a script that runs before this bundle loads and nothing
   * mutates afterwards. It is constant for the lifetime of the document, so
   * the hook order is stable — which is the property the rule actually
   * protects.
   */
  if (propelAuthUrl() === null) return null;

  const auth = useAuthInfo();
  if (auth.loading || !auth.isLoggedIn) return null;

  const { user } = auth;
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return { email: user.email, name: name.length > 0 ? name : null };
}

/**
 * The auth provider cannot be reached.
 *
 * Says the two things that are actually wrong when this happens, because both
 * are settings somebody has to go and change and neither is visible from the
 * page: the tenant URL the portal was given, and whether this origin is on
 * PropelAuth's allow-list.
 */
const ProviderUnreachable: React.FC = () => (
  <div className="authwrap">
    <div className="authcard">
      <h1 className="auth__h1">Cannot reach the sign-in service</h1>
      <p className="auth__sub">
        The portal is pointed at <code>{propelAuthUrl()}</code> and is not getting an answer.
      </p>
      <p className="auth__hint">
        Two things cause this. The URL may be wrong — it must match the API's <code>PROPELAUTH_AUTH_URL</code>. Or this
        address may not be listed under Frontend Integration in the PropelAuth dashboard, in which case the browser
        blocks the request and the console says so.
      </p>
      <button type="button" className="btn btn-primary auth__submit" onClick={() => window.location.reload()}>
        Try again
      </button>
    </div>
  </div>
);
